import * as firebase from "@/firebase";
import { cloneDeep, get } from 'lodash';
import shortid from "shortid";
import { createNewGameState, createPlayersState} from './schema';
import { getAdjacentTiles, getRandomTile } from "@/store/utils";
import TooManyPlayers from '@/exceptions/TooManyPlayers';
import TooManyStocks from "@/exceptions/TooManyStocks";
import NoStocksAvailable from "@/exceptions/NoStocksAvailable";
import InsufficientFunds from "@/exceptions/InsufficientFunds";
import HotelUnavailable from "@/exceptions/HotelUnavailable";
import {EDGE_OF_BOARD} from "@/store/game/constants";

async function registerPlayerDisconnectedListener ({ getters }) {
  getters.playerRef.onDisconnect()
    .update({
      isOnline: false,
    });
  // getters.playerRef.once('value', function(snapshot) {
  //   if (snapshot.exists()){
  //     getters.playerRef.onDisconnect()
  //       .update({
  //         isOnline: false,
  //       });
  //   }
  // });
}

function recursivelyGetChildTiles(boardTiles, startingTileId, tilesToUpdate = {}, hotelIdToRemove = false) {
  const adjacentTileIds = getAdjacentTiles(startingTileId);

  Object.values(adjacentTileIds).forEach(adjacentTileId => {
    // If the adjacent tile is an edge of the board
    if (adjacentTileId === EDGE_OF_BOARD) {
      return;
    }

    const tile = boardTiles[adjacentTileId];

    if (!tile) {
      throw new Error('WTF Tile undefined, that shouldnt happen');
    }

    // The tile has already been checked in a previous iteration, skip it.
    if (tilesToUpdate[tile.id]) {
      return;
    }

    // This happens when a tile is place next to an existing chain.
    // We want to update any tiles that that have just been joined to the chain, but not replace existing tiles
    const isPartOfAHotel = tile.isChild === true || tile.hotel !== false;
    if (isPartOfAHotel && !hotelIdToRemove) {
      return;
    }

    // In the middle of a merger. If the tile we are looking at is not part of the hotel we are trying to remove, skip it.
    if (hotelIdToRemove !== tile.hotel) {
      return;
    }

    if (tile.isEmpty) {
      return;
    }

    tilesToUpdate[tile.id] = true;
    recursivelyGetChildTiles(boardTiles, tile.id, tilesToUpdate, hotelIdToRemove);
  });

  return tilesToUpdate;
  // if tile.isChild = true || tile.hotel = true, throw an error. This should not happen. Means something when terribly wrong

  // if this.isEmpty, return, you have reached the end of the chain

  // Update the child tile isChild = true, .hotel = hotelId
  // call recursivelyGetChildTiles with the current tile to update all it's children
}

export default {
  registerListener({ commit, getters, dispatch }) {
    if (!getters.gameId) {
      throw new Error('gameId not set');
    }

    commit('setGameRef', firebase.rtDb.ref(`${firebase.rtDbRootNode}/${getters.gameId}`));

    // This player has previously joined this game
    if (getters.localStoragePlayerId) {
      dispatch('setPlayerRef', getters.localStoragePlayerId);
      dispatch('setPlayerOnline');
      registerPlayerDisconnectedListener({ getters });
    }

    getters.gameRef.on('value', function(snapshot) {
      if(!snapshot.exists()) {
        console.error('data no existe');
      }

      console.log('GAME STATE', snapshot.val());
      commit('setGameState', snapshot.val());
      commit('gameStateHasLoaded', true);
    });
  },

  async createNewGame({ commit }, numberOfPlayers) {
    const gameId = shortid.generate();
    const gameState = createNewGameState(gameId, numberOfPlayers);

    await firebase.rtDb
      .ref(`${firebase.rtDbRootNode}/${gameId}`)
      .set(gameState);

    commit('setGameState', gameState);
  },

  // async updateNumberOfPlayers({ state }, numberOfPlayers) {
  //   // TODO: only add and remove players if needed
  //   const players = {};
  //   for(let i = 0; i<numberOfPlayers; i++) {
  //     const playerId = shortid.generate();
  //     players[playerId] = createPlayersState(playerId);
  //   }
  //
  //   state.gameRef.update({
  //     numberOfPlayers,
  //     players
  //   })
  // },

  async startGame({ state, getters }) {
    if (state.gameState.hasStarted){
      console.debug('Game has already started');
      return;
    }

    state.gameRef.update({ hasStarted: true });

    // distribute starting tiles
    const gameState = cloneDeep(state.gameState);
    Object.values(gameState.players).forEach(player => {
      for (let i=0; i < 6; i++) {
        const data = getRandomTile(gameState.tiles);
        gameState.tiles = data.tiles;

        if (!player.tiles) {
          player.tiles = [];
        }
        player.tiles.push(data.tile);
      }
    });

    // pick starting player at random
    gameState.whosTurn = getters.playerOrder[0];
    // gameState.turnOrder = Object.values(getters.players).map(player => {
    //   return player.id;
    // });
    state.gameRef.update(gameState);
  },

  async updateGameState({ state }, { property, value }) {
    await state.gameRef
      .update({
        [property]: value,
      });
  },

  async joinGame({ getters, dispatch }, { name }) {
    if (Object.values(getters.players).length >= getters.numberOfPlayers) {
      throw new TooManyPlayers();
    }

    const player = createPlayersState(name);
    dispatch('setPlayerRef', player.id);

    dispatch('saveGameDataToLocalStorage', {
      gameId: getters.gameId,
      playerId: player.id
    });

    const playerOrder = cloneDeep(getters.playerOrder);
    playerOrder.push(player.id);
    await getters.gameRef.update({ playerOrder });

    await getters.playerRef.update(player);
    registerPlayerDisconnectedListener({ getters });
  },

  rejoinGame({ getters, dispatch }, playerId) {
    dispatch('setPlayerRef', playerId);
    dispatch('saveGameDataToLocalStorage', {
      gameId: getters.gameId,
      playerId,
    });

    registerPlayerDisconnectedListener({ getters });
  },

  loadGameDataFromLocalStorage({ commit }) {
    let gameData = JSON.parse(localStorage.getItem('gameData'));
    if (!gameData) {
      gameData = [];
    }

    commit('setLocalStorage', gameData);
  },

  saveGameDataToLocalStorage({ state, commit }, { gameId, playerId }) {
    commit('addGameToLocalStorage', { gameId, playerId });
    localStorage.setItem('gameData', JSON.stringify(state.localStorage));
  },

  removeGameDataFromLocalStorage({ getters, dispatch }) {
    let gameData = JSON.parse(localStorage.getItem('gameData'));

    gameData = gameData.filter((data) => {
      return data.gameId !== getters.gameId;
    });

    localStorage.setItem('gameData', JSON.stringify(gameData));

    dispatch('loadGameDataFromLocalStorage');
  },

  setPlayerRef({ getters, commit }, playerId) {
    const playerRef = getters.gameRef.child(`players/${playerId}`);
    commit('setPlayerRef', playerRef);
  },

  setPlayerOffline({ getters }) {
    if (!getters.playerRef) {
      return;
    }

    getters.playerRef.update({
      isOnline: false
    });
  },

  setPlayerOnline({ getters }) {
    getters.playerRef.update({
      isOnline: true,
    })
  },

  kickPlayer({ getters }, playerId) {
    getters.gameRef.child(`players/${playerId}`).remove();
  },

  handleKickPlayer({ getters, commit, dispatch }) {
    // Remove player from play order
    const localPlayerId = getters.localPlayerId;
    const playerOrder = cloneDeep(getters.playerOrder);
    const localPlayerStocks = playerOrder.indexOf(localPlayerId);
    playerOrder.splice(localPlayerStocks, 1);
    getters.gameRef.update({ playerOrder });

    // Delete player
    getters.playerRef.onDisconnect().cancel();
    commit('setPlayerRef', null);
    dispatch('removeGameDataFromLocalStorage');
  },

  async setSelectedTile({ getters }, tileId) {
    await getters.gameRef.update({
      'selectedTile': tileId,
    })
  },

  async addStockToBePurchased({ getters }, hotelId) {
    let stocksToBePurchased = cloneDeep(getters.stocksToBePurchased);

    // See if a company exists on the board, if not show message it can't be bought.
    const isHotelOnTheBoard = get(getters, `tilesPerHotel[${hotelId}].length`, false);
    if (!isHotelOnTheBoard) {
      throw new HotelUnavailable();
    }

    const quantityAvailable = getters.stocks[hotelId].quantity;
    if (quantityAvailable <= 0) {
      throw new NoStocksAvailable();
    }

    const stockPrice = getters.stockMoneyPerHotel[hotelId].price;
    const potentialBalance = Number(getters.localPlayer.money - getters.totalPrice - stockPrice);
    if (potentialBalance < 0) {
      throw new InsufficientFunds();
    }

    if (isNaN(potentialBalance)){
      throw new Error('isNaN(potentialBalance) === true');
    }

    if (stocksToBePurchased.length >= 3) {
      throw new TooManyStocks();
    }

    stocksToBePurchased.push(hotelId);
    await getters.gameRef.update({
      stocksToBePurchased,
      [`stocks/${hotelId}/quantity`]: (quantityAvailable - 1),
    });
  },

  async removeStockToBePurchased({ getters }, arrayIndex) {
    const stocks = cloneDeep(getters.stocksToBePurchased);
    const hotelId = stocks[arrayIndex];
    stocks.splice(arrayIndex, 1);

    await getters.gameRef.update({
      'stocksToBePurchased': stocks,
      [`stocks/${hotelId}/quantity`]: (getters.stocks[hotelId].quantity + 1),
    });
  },

  async updateMultipleProperties({ getters }, data) {
    await getters.gameRef.update(data);
  },

  async giveBonusStock({ getters }, hotelId) {
    if(!hotelId) {
      throw new Error('hotelId required');
    }

    const quantityAvailable = getters.stocks[hotelId].quantity;
    if (quantityAvailable <= 0) {
      throw new NoStocksAvailable();
    }

    let playerStockQuantity = cloneDeep(getters.localPlayerStocks[hotelId]);
    await getters.gameRef.update({
      [`stocks/${hotelId}/quantity`]: (quantityAvailable - 1),
      [`players/${getters.localPlayerId}/stocks/${hotelId}`]: (playerStockQuantity + 1),
    });
  },

  /**
   * Update all adjacent tiles, then update the tiles adjacent to those tiles
   */
  async updateChildTiles({ getters }, { startingTileId, newTile, handleParentTile = false, hotelIdToRemove = false }) {
    const adjacentTiles = recursivelyGetChildTiles(getters.board, startingTileId, { [startingTileId]: true }, hotelIdToRemove);

    const tiles = Object.keys(adjacentTiles).reduce((obj, tile) => {
      obj[tile] = {
        ...newTile,
        id: tile,
      };

      // Make sure the tile where the user placed the piece is the Parent
      if (handleParentTile && tile === startingTileId) {
        obj[tile].isChild = false;
      }

      return obj;
    }, {});

    await getters.gameRef.child('board').update({
      ...tiles
    });
  },

  /**
   * @param getters
   * @param {createEvent()} event
   * @returns {Promise<void>}
   */
  async addEvent({ getters }, event) {
    const eventId = getters.nextEventId;
    await getters.gameRef.child('events').update({
      [eventId]: {
        ...event,
        _id: eventId,
      }
    });
  }
}
